import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	type ArtemisApprovalFilterInput,
	type ArtemisApprovalSortInput,
	GetApprovalDocument,
	GetApprovalsDocument,
	ResolveApprovalDocument,
	type ResolveApprovalInput,
} from "@/common/artemis-sdk/graphql";
import type { Approval } from "../model";
import { useGqlClient } from "../use-gql-client";
import { buildWhere, toOrder, usePagedList } from "./use-paged-list";

/**
 * Fetches the first page of approvals for a workspace (lookup use — e.g. finding a task's
 * approval). For the paginated approvals table use {@link useApprovalsPaged}.
 */
export function useApprovals(workspaceId: string) {
	const gqlFetch = useGqlClient();

	return useQuery({
		queryKey: ["approvals", workspaceId],
		queryFn: async () => {
			const data = await gqlFetch(GetApprovalsDocument, { workspaceId });
			return (data.approvals?.nodes ?? []) as Approval[];
		},
		enabled: !!workspaceId,
	});
}

/**
 * Fetches a single approval by ID, including the structured credentialRequest payload (requested
 * IAM actions/resources) that the list query omits. Used by the details drawer.
 */
export function useApproval(approvalId?: string) {
	const gqlFetch = useGqlClient();

	return useQuery({
		queryKey: ["approval", approvalId],
		queryFn: async () => {
			const data = await gqlFetch(GetApprovalDocument, {
				approvalId: approvalId as string,
			});
			return data.approval as Approval;
		},
		enabled: !!approvalId,
	});
}

/**
 * Server-side paginated approvals list for a workspace: Next/Prev cursor pagination plus
 * server-side search (name/id/taskId), status/riskLevel filters, and sort.
 */
export function useApprovalsPaged(
	workspaceId: string,
	initialSearch?: string,
	initialStatus?: string,
) {
	const gqlFetch = useGqlClient();
	return usePagedList<Approval>({
		baseKey: ["approvals-paged", workspaceId],
		enabled: !!workspaceId,
		initialSort: [{ id: "createdAt", desc: true }],
		initialSearch,
		initialFilters: { status: initialStatus },
		fetchPage: async ({ debouncedSearch, filters, sort, window }) => {
			const data = await gqlFetch(GetApprovalsDocument, {
				workspaceId,
				...window,
				order: toOrder<ArtemisApprovalSortInput>(sort),
				where: buildWhere(debouncedSearch, ["name", "id", "taskId"], filters) as
					| ArtemisApprovalFilterInput
					| undefined,
			});
			const c = data.approvals;
			return {
				nodes: (c?.nodes ?? []) as Approval[],
				totalCount: c?.totalCount ?? 0,
				pageInfo: c?.pageInfo ?? { hasNextPage: false, hasPreviousPage: false },
			};
		},
	});
}

/**
 * Approves or rejects a pending approval, then refreshes the approvals list.
 */
export function useResolveApproval(workspaceId: string) {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			approvalId,
			input,
		}: {
			approvalId: string;
			input: ResolveApprovalInput;
		}) => {
			const data = await gqlFetch(ResolveApprovalDocument, {
				approvalId,
				input,
			});
			return data.resolveApproval.approval;
		},
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: ["approvals", workspaceId] }),
	});
}
